import * as React from 'react';
import { useState, useEffect, useCallback, useContext } from 'react';
import { CloudCheckIcon, CloudIcon, CloudWarningIcon } from '@phosphor-icons/react';
import { Tooltip } from '@heroui/tooltip';
import { FetchWithVersionCheck } from '@/shared/functions';
import { AuthContext } from '@/app/contexts';
export interface SyncStatus {
  orgId: string;
  tenantId: string | null;
  tenantName: string | null;
  driveType: string;
  sourcePath: string;
  isEnabled: boolean;
  lastSyncStart: number | null;
  lastSyncEnd: number | null;
  lastFileCopiedTime: number | null;
  lastFileCopiedName: string | null;
}
type TenantRollupStatus = 'ENABLED' | 'DISABLED' | 'MIXED';
interface TenantRollup {
  tenantKey: string;
  tenantLabel: string;
  total: number;
  enabled: number;
  disabled: number;
  status: TenantRollupStatus;
  rows: SyncStatus[];
}
const tooltipClassNames = {
  content: 'bg-tooltip-bg text-white',
  base: 'before:bg-tooltip-bg'
};

/**
 * Fetches sync status from /api/protected/sync when enabled.
 * Returns syncStatus rows and a refetch function (e.g. for onMouseEnter).
 */
export function useSyncStatus(enabled: boolean) {
  const {
    accessToken
  } = useContext(AuthContext);
  const [syncStatus, setSyncStatus] = useState<SyncStatus[] | null>(null);
  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await FetchWithVersionCheck('/api/protected/sync', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        }
      });
      const data = await response.json();
      if (!response.ok || !data) {
        setSyncStatus(null);
        return;
      }
      if (data?.rows?.length) {
        setSyncStatus(data.rows);
      } else {
        setSyncStatus(null);
      }
    } catch {
      setSyncStatus(null);
    }
  }, [accessToken]);
  useEffect(() => {
    if (enabled) {
      fetchSyncStatus();
    }
  }, [enabled, fetchSyncStatus]);
  return {
    syncStatus,
    refetch: fetchSyncStatus
  };
}
export interface SyncStatusIndicatorProps {
  syncStatus: SyncStatus[] | null;
  iconSize?: number;
  /** Rendered when syncStatus is disabled or empty (e.g. default cloud icon or placeholder) */
  fallback: React.ReactNode;
}

/**
 * Renders the sync status tooltip and icon (or fallback when no data).
 * Use with useSyncStatus() so sync logic lives in one place.
 */
export function SyncStatusIndicator({
  syncStatus,
  iconSize = 20,
  fallback
}: SyncStatusIndicatorProps) {
  if (!syncStatus?.length) {
    return <>{fallback}</>;
  }
  const tenantRollups = syncStatus.reduce<TenantRollup[]>((acc, row) => {
    const tenantKey = row.tenantId ?? `org:${row.orgId}`;
    const tenantLabel = row.tenantName ?? (row.tenantId ? row.tenantId : 'Organization-level');
    const existing = acc.find(rollup => rollup.tenantKey === tenantKey);
    if (existing) {
      existing.rows.push(row);
      existing.total += 1;
      if (row.isEnabled) {
        existing.enabled += 1;
      } else {
        existing.disabled += 1;
      }
      return acc;
    }
    acc.push({
      tenantKey,
      tenantLabel,
      total: 1,
      enabled: row.isEnabled ? 1 : 0,
      disabled: row.isEnabled ? 0 : 1,
      status: row.isEnabled ? 'ENABLED' : 'DISABLED',
      rows: [row]
    });
    return acc;
  }, []);
  tenantRollups.forEach(rollup => {
    rollup.status = rollup.enabled > 0 && rollup.disabled > 0 ? 'MIXED' : rollup.enabled > 0 ? 'ENABLED' : 'DISABLED';
  });
  const content = <div className="flex flex-col gap-2 p-2">
      {tenantRollups.map(tenant => <div key={tenant.tenantKey} className="p-2 border border-solid border-white/30">
          <div className="font-semibold">
            {tenant.tenantLabel}: {tenant.status}
          </div>
          <div>
            Enabled {tenant.enabled} / Disabled {tenant.disabled} / Total {tenant.total}
          </div>
          <div className="mt-2 flex flex-col gap-2">
            {tenant.rows.map((item, index) => <div key={`${tenant.tenantKey}-${index}`} className={`p-2 border border-solid ${item.isEnabled ? 'border-green-500' : 'border-red-500'}`}>
                <div>
                  {item.driveType} - {item.sourcePath || 'No source path'}
                </div>
                <div>Status: {item.isEnabled ? 'ENABLED' : 'DISABLED'}</div>
                <div>
                  {item.lastSyncStart ? `Last sync start: ${new Date(item.lastSyncStart * 1000).toLocaleString()}` : 'No sync start'}
                </div>
                <div>
                  {item.lastSyncEnd ? `Last sync end: ${new Date(item.lastSyncEnd * 1000).toLocaleString()}` : 'No sync end'}
                </div>
                {item.lastFileCopiedTime != null && item.lastFileCopiedName && <div className="flex flex-col items-start justify-between gap-1">
                    <div>
                      Last copy: {new Date(item.lastFileCopiedTime * 1000).toLocaleString()}
                    </div>
                    <div>
                      File copied:{' '}
                      {item.lastFileCopiedName.slice(item.lastFileCopiedName.lastIndexOf('/') + 1)}
                    </div>
                  </div>}
              </div>)}
          </div>
        </div>)}
    </div>;
  const tenantStatuses = tenantRollups.map(tenant => tenant.status);
  const hasMixedTenants = tenantStatuses.includes('MIXED');
  const allTenantsEnabled = tenantStatuses.length > 0 && tenantStatuses.every(status => status === 'ENABLED');
  const allTenantsDisabled = tenantStatuses.length > 0 && tenantStatuses.every(status => status === 'DISABLED');
  const icon = allTenantsEnabled ? <CloudCheckIcon size={iconSize} className="text-green-500" /> : hasMixedTenants ? <CloudWarningIcon size={iconSize} className="text-yellow-500" /> : allTenantsDisabled ? <CloudWarningIcon size={iconSize} className="text-red-500" /> : <CloudIcon size={iconSize} className="text-gray-500" />;
  const trigger = <div>{icon}</div>;
  return <Tooltip classNames={tooltipClassNames} offset={10} placement="left" showArrow content={content} data-sentry-element="Tooltip" data-sentry-component="SyncStatusIndicator" data-sentry-source-file="sync-status.tsx">
      {trigger}
    </Tooltip>;
}